
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import Header from "@/components/Header";
import { usePaymentProcessor } from "@/hooks/payment/usePaymentProcessor";
import PaymentContainer from "@/components/payment/PaymentContainer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { isIOSDevice } from "@/utils/deviceDetection";

// Load Stripe outside of component to avoid recreation on renders
const stripePromise = loadStripe("pk_live_51LJCA6HT9Nk9Fhr3VpL0hlFs9p5eNLGn9A3fhuCJ8ATkwg1b6V2VpPb6oOhd0G9FZYGcejxPSCbzbJT9D4AFGUcv00mgoB3ni2");

// Define which payment methods are processed through Stripe
const stripePaymentMethods = ["stripe", "google-pay", "apple-pay"];

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if device is iOS
  const isIOS = isIOSDevice();
  
  // Get strategy and payment plan from location state
  const strategy = location.state?.strategy;
  const isYearly = location.state?.isYearly;
  
  // Use our payment processor hook
  const {
    amount,
    isProcessing,
    paymentError,
    handleStripeSubmit,
    handleManualSubmit,
    setPaymentError
  } = usePaymentProcessor({ strategy, isYearly });

  // For iOS devices, redirect to account page and show a message
  useEffect(() => {
    if (isIOS) {
      toast({
        title: "iOS Payment Notice",
        description: "In-app purchases are not available on iOS devices. Please manage your subscription through your account.",
      });
      navigate('/account');
    }
  }, [isIOS, navigate, toast]);
  
  // Check if user has an active subscription
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setIsLoading(true);
        
        // First check if user is authenticated
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }
        
        // Check for active subscription
        const { data: subscriptions, error } = await supabase
          .from('strategy_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('active', true)
          .limit(1);
          
        if (error) {
          console.error("Error checking subscription:", error);
          toast({
            title: "Error",
            description: "Could not verify subscription status",
            variant: "destructive"
          });
        } else if (subscriptions && subscriptions.length > 0) {
          console.log("Active subscription found, redirecting to thank-you page");
          // User has an active subscription, redirect to thank-you page
          setTimeout(() => {
            navigate('/thank-you', {
              state: {
                strategy,
                isYearly: subscriptions[0].plan === 'yearly'
              }
            });
          }, 100);
        }
      } catch (err) {
        console.error("Unexpected error checking subscription:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (strategy) {
      checkSubscription();
    } else {
      setIsLoading(false);
    }
  }, [strategy, navigate, toast]);

  // If it's an iOS device, redirect immediately
  if (isIOS) {
    return null; // Return null as we're redirecting in useEffect
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Header />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#517FA4] mx-auto"></div>
          <p className="mt-4 text-[#517FA4]">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />
      <main>
        <PaymentContainer 
          strategy={strategy}
          isYearly={isYearly}
          amount={amount}
          paymentError={paymentError}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          isProcessing={isProcessing}
          stripePromise={stripePromise}
          stripePaymentMethods={stripePaymentMethods}
          handleStripeSubmit={handleStripeSubmit}
          handleManualSubmit={handleManualSubmit}
        />
      </main>
    </div>
  );
};

export default Payment;
